import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Filter, User, RefreshCcw } from "lucide-react";

const ACTION_COLORS = {
  create: "text-emerald-400 border-emerald-400/40",
  update: "text-amber-400 border-amber-400/40",
  delete: "text-red-400 border-red-400/40",
};

const ENTITY_LABEL = {
  player: "Jugador",
  team: "Equipo",
  category: "Categoría",
  jornada: "Jornada",
};

const ACTION_LABEL = {
  create: "Creación",
  update: "Edición",
  delete: "Eliminación",
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs?limit=300");
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (entityFilter && l.entity_type !== entityFilter) return false;
      if (actionFilter && l.action !== actionFilter) return false;
      return true;
    });
  }, [logs, entityFilter, actionFilter]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline">Trazabilidad</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Historial de cambios</h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-xl">
            Registro cronológico de creaciones, ediciones y eliminaciones sobre categorías, equipos, jugadores y jornadas dentro del club.
          </p>
        </div>
        <button
          type="button"
          data-testid="audit-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 h-9 px-4 border border-border hover:border-primary hover:text-primary transition-colors text-xs uppercase font-heading tracking-wider"
        >
          <RefreshCcw size={13} /> Refrescar
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          <Filter size={13} className="text-muted-foreground" />
          <span className="overline text-[10px]">Filtros</span>
        </div>
        <select
          data-testid="audit-entity-filter"
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="h-8 bg-card border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none"
        >
          <option value="">Todas las entidades</option>
          <option value="player">Jugadores</option>
          <option value="team">Equipos</option>
          <option value="category">Categorías</option>
          <option value="jornada">Jornadas</option>
        </select>
        <select
          data-testid="audit-action-filter"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="h-8 bg-card border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none"
        >
          <option value="">Todas las acciones</option>
          <option value="create">Creación</option>
          <option value="update">Edición</option>
          <option value="delete">Eliminación</option>
        </select>
        <span className="text-xs text-muted-foreground font-mono ml-2">{filtered.length} / {logs.length}</span>
      </div>

      <div className="border border-border" data-testid="audit-log-table">
        <div className="grid grid-cols-12 border-b border-border bg-white/[0.02] px-4 py-2 overline text-[10px]">
          <div className="col-span-3">Fecha</div>
          <div className="col-span-2">Acción</div>
          <div className="col-span-2">Entidad</div>
          <div className="col-span-3">Elemento</div>
          <div className="col-span-2">Usuario</div>
        </div>
        {loading && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <ScrollText size={28} className="mx-auto mb-2 opacity-40" /> Cargando historial…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <ScrollText size={28} className="mx-auto mb-2 opacity-40" />
            Sin registros para los filtros seleccionados.
          </div>
        )}
        {!loading && filtered.map((l) => (
          <div key={l.id} data-testid={`audit-row-${l.id}`}
               className="grid grid-cols-12 items-center px-4 py-3 border-b border-border/60 last:border-0 text-xs hover:bg-white/[0.02] transition-colors">
            <div className="col-span-3 font-mono text-muted-foreground">
              {formatDate(l.timestamp)}
            </div>
            <div className="col-span-2">
              <span className={`inline-block border px-2 py-0.5 font-heading uppercase tracking-wide text-[10px] ${ACTION_COLORS[l.action] || "border-border text-muted-foreground"}`}>
                {ACTION_LABEL[l.action] || l.action}
              </span>
            </div>
            <div className="col-span-2 font-mono">
              {ENTITY_LABEL[l.entity_type] || l.entity_type}
            </div>
            <div className="col-span-3 font-heading font-semibold uppercase tracking-tight text-sm truncate" title={l.entity_name}>
              {l.entity_name || "—"}
            </div>
            <div className="col-span-2 flex items-center gap-2 min-w-0">
              <User size={12} className="text-muted-foreground shrink-0" />
              <div className="truncate">
                <div className="truncate">{l.user_name || "—"}</div>
                <div className="overline text-[9px]">{l.user_role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
